/**
 * Number Pattern Analysis — Detects "model collapse" in question numbers.
 *
 * Identifies when AI-generated questions use only "nice" numbers:
 * - Round numbers (10, 20, 50, 100)
 * - Simple decimals (,0 or ,5 only)
 * - Simple fractions (1/2, 1/4, 1/3 only)
 * - Missing edge cases (0, 1, overflow)
 *
 * Used by supportStage.ts for family-level validation.
 */

import { SEVERITY } from '../pipeline/severityScale'

export interface NumberPatternAnalysis {
  totalQuestions: number
  roundNumbersCount: number // Questions with only 10, 20, 50, 100
  halfDecimalsCount: number // Questions with only ,0 or ,5 decimals
  simpleFractionsCount: number // Questions with only 1/2, 1/4, 1/3
  hasZero: boolean // At least one question uses 0
  hasOne: boolean // At least one question uses 1
  hasOverflow: boolean // At least one question has tiental/honderdtal overflow
  hasUglyNumbers: boolean // At least one question uses "ugly" numbers (13, 27, 3.47)
}

/**
 * Extract all numbers from a text string.
 * Handles integers, decimals (Dutch comma notation), and fractions.
 */
function extractNumbers(text: string): number[] {
  const numbers: number[] = []

  // Match integers and decimals (Dutch notation: 3,47 or 3.427)
  // Exclude thousand separators (3.427 = three thousand)
  const decimalMatches = text.matchAll(/\b(\d+)[,.](\d+)\b/g)
  for (const match of decimalMatches) {
    const whole = parseInt(match[1], 10)
    const decimal = match[2]
    // If decimal part is exactly 3 digits, it's likely a thousand separator
    if (decimal.length === 3) {
      numbers.push(whole * 1000 + parseInt(decimal, 10))
    } else {
      numbers.push(whole + parseInt(decimal, 10) / Math.pow(10, decimal.length))
    }
  }

  // Match integers (not already captured as part of decimals)
  const integerMatches = text.matchAll(/\b(\d+)\b/g)
  for (const match of integerMatches) {
    const num = parseInt(match[1], 10)
    // Only add if not already captured as part of a decimal
    if (!numbers.some((n) => Math.abs(n - num) < 0.01)) {
      numbers.push(num)
    }
  }

  // Match fractions (e.g., 3/4, 1/2)
  const fractionMatches = text.matchAll(/\b(\d+)\/(\d+)\b/g)
  for (const match of fractionMatches) {
    const numerator = parseInt(match[1], 10)
    const denominator = parseInt(match[2], 10)
    if (denominator !== 0) {
      numbers.push(numerator / denominator)
    }
  }

  return numbers
}

/**
 * Check if a number is "round" (10, 20, 50, 100, 200, etc.)
 */
function isRoundNumber(num: number): boolean {
  if (num === 0) return false // 0 is an edge case, not a "round" number
  if (num < 10) return false // Single digits are not "round"

  // Check if divisible by 10, 20, 50, or 100
  return (
    num % 100 === 0 || // 100, 200, 300
    num % 50 === 0 || // 50, 150, 250
    num % 20 === 0 || // 20, 40, 60
    num % 10 === 0 // 10, 30, 70
  )
}

/**
 * Check if a decimal only uses ,0 or ,5
 */
function isHalfDecimal(num: number): boolean {
  const decimalPart = num - Math.floor(num)
  return Math.abs(decimalPart) < 0.01 || Math.abs(decimalPart - 0.5) < 0.01
}

/**
 * Check if a fraction is "simple" (1/2, 1/4, 1/3, 3/4)
 */
function isSimpleFraction(num: number): boolean {
  const commonFractions = [
    1 / 2, // 0.5
    1 / 3, // 0.333...
    1 / 4, // 0.25
    3 / 4, // 0.75
    2 / 3, // 0.666...
  ]
  return commonFractions.some((f) => Math.abs(num - f) < 0.01)
}

/**
 * Check if a number is "ugly" (not round, not half-decimal, not simple fraction)
 */
function isUglyNumber(num: number): boolean {
  if (num === 0 || num === 1) return false // Edge cases, not "ugly"
  return !isRoundNumber(num) && !isHalfDecimal(num) && !isSimpleFraction(num)
}

/**
 * Check if a question involves tiental/honderdtal overflow.
 * Examples: 18+5 (crosses 20), 97+8 (crosses 100)
 */
function hasOverflow(numbers: number[]): boolean {
  // Look for pairs of numbers that would cause overflow when added
  for (let i = 0; i < numbers.length - 1; i++) {
    for (let j = i + 1; j < numbers.length; j++) {
      const a = numbers[i]
      const b = numbers[j]
      const sum = a + b

      // Check if crossing a ten boundary (e.g., 18+5=23)
      if (Math.floor(a / 10) !== Math.floor(sum / 10)) return true

      // Check if crossing a hundred boundary (e.g., 97+8=105)
      if (Math.floor(a / 100) !== Math.floor(sum / 100)) return true
    }
  }
  return false
}

/**
 * Analyze number patterns across all questions in a family.
 * Returns metrics about "model collapse" (overuse of nice numbers).
 */
export function analyzeNumberPatterns(
  questions: Array<{ question: string; options?: string[] }>,
): NumberPatternAnalysis {
  let roundNumbersCount = 0
  let halfDecimalsCount = 0
  let simpleFractionsCount = 0
  let hasZero = false
  let hasOne = false
  let foundOverflow = false
  let hasUglyNumbers = false

  for (const q of questions) {
    // Extract numbers from question text and options
    const fullText = q.question + ' ' + (q.options ? q.options.join(' ') : '')
    const numbers = extractNumbers(fullText)

    if (numbers.length === 0) continue

    // Check for edge cases
    if (numbers.includes(0)) hasZero = true
    if (numbers.includes(1)) hasOne = true

    // Check for overflow
    if (hasOverflow(numbers)) foundOverflow = true

    // Check for ugly numbers
    if (numbers.some(isUglyNumber)) hasUglyNumbers = true

    // Check if ALL numbers in this question are "nice"
    const allRound = numbers.every((n) => n === 0 || isRoundNumber(n))
    const allHalfDecimals = numbers.every(
      (n) => Number.isInteger(n) || isHalfDecimal(n),
    )
    const allSimpleFractions = numbers.every(
      (n) => Number.isInteger(n) || isSimpleFraction(n),
    )

    if (allRound) roundNumbersCount++
    if (allHalfDecimals) halfDecimalsCount++
    if (allSimpleFractions) simpleFractionsCount++
  }

  return {
    totalQuestions: questions.length,
    roundNumbersCount,
    halfDecimalsCount,
    simpleFractionsCount,
    hasZero,
    hasOne,
    hasOverflow: foundOverflow,
    hasUglyNumbers,
  }
}

/**
 * Determine if a family shows signs of "model collapse" (overuse of nice numbers).
 * Returns a severity score (0-100) and a description of the issue.
 */
export function detectNumberPatternCollapse(analysis: NumberPatternAnalysis): {
  severity: number
  issue: string | null
} {
  if (analysis.totalQuestions < 3) {
    return { severity: 0, issue: null } // Too few questions to judge
  }

  const roundPct = analysis.roundNumbersCount / analysis.totalQuestions
  const halfDecimalPct = analysis.halfDecimalsCount / analysis.totalQuestions
  const simpleFractionPct =
    analysis.simpleFractionsCount / analysis.totalQuestions

  // High severity: ≥80% use only nice numbers
  if (roundPct >= 0.8) {
    return {
      severity: SEVERITY.NEAR_MEDIUM,
      issue: `${(roundPct * 100).toFixed(0)}% of questions use only round numbers (10, 20, 50, 100)`,
    }
  }

  if (halfDecimalPct >= 0.8) {
    return {
      severity: SEVERITY.LOW,
      issue: `${(halfDecimalPct * 100).toFixed(0)}% of questions use only half-decimals (,0 or ,5)`,
    }
  }

  if (simpleFractionPct >= 0.8) {
    return {
      severity: SEVERITY.LOW,
      issue: `${(simpleFractionPct * 100).toFixed(0)}% of questions use only simple fractions (1/2, 1/4, 3/4)`,
    }
  }

  // Medium severity: Missing edge cases
  if (analysis.totalQuestions >= 5 && !analysis.hasZero && !analysis.hasOne) {
    return {
      severity: SEVERITY.MINOR,
      issue: 'No questions test edge cases (0 or 1)',
    }
  }

  if (analysis.totalQuestions >= 5 && !analysis.hasUglyNumbers) {
    return {
      severity: SEVERITY.NEAR_MINOR,
      issue:
        'No questions use "ugly" numbers (13, 27, 3.47) — all numbers are too "nice"',
    }
  }

  return { severity: 0, issue: null }
}
